import { useToast } from '../Toast/ToastContext';
import type { DocumentSection } from '../../../core/types/data-models';

/** Renders the document title and its section tree (in order) as a single Markdown document. */
export function buildDocumentMarkdown(
  docName: string,
  rootSections: DocumentSection[],
  childrenByParent: Map<number, DocumentSection[]>,
): string {
  const lines: string[] = [`# ${docName}`];

  const renderSection = (section: DocumentSection, depth: number) => {
    lines.push('', `${'#'.repeat(Math.min(depth + 1, 6))} ${section.title}`);
    if (section.content) lines.push('', section.content);
    const children = (childrenByParent.get(section.id) ?? []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    children.forEach((child) => renderSection(child, depth + 1));
  };

  rootSections
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .forEach((section) => renderSection(section, 1));

  return lines.join('\n');
}

/** Copies Markdown to the clipboard and reports the outcome in a toast. */
export function useCopyMarkdown() {
  const showToast = useToast();
  return async (markdown: string) => {
    try {
      await navigator.clipboard.writeText(markdown);
      showToast({ title: 'Copied as Markdown' });
    } catch {
      showToast({ title: 'Failed to copy to clipboard', severity: 'error' });
    }
  };
}
